import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons/faExternalLinkAlt';

import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import messages from './carrier-insurance-banner-messages';
import style from './carrier-insurance-banner.module.less';
import { SwitchToParcelGuardButton } from './switch-to-parcel-guard-button';
import HelpArticleLink from '../../../../components/help-article-link';
import { US } from '../../../../constants/country-codes';
import { InsuranceProviders } from '../../../../enums/insurance-providers';
import { useEnableCarrierInsuranceBanner } from '../../../../feature-flags/hooks/use-enable-carrier-insurance-banner';
import { linkIDs } from '../../../../services/link-service/links';
import Global from '../../../../store/global-selectors';
import { useWalletEligibility } from '../../../../wallet/hooks/use-wallet-eligibility';
import { ACTION_TYPE_INSURE_PACKAGE } from '../constants/action-types';
import { AutomationRulesContext } from '../context';

export const CarrierInsuranceBanner = () => {
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const { automationRules, refreshAutomationRules } = useContext(AutomationRulesContext);
  const walletEligibility = useWalletEligibility();
  const enableCarrierInsuranceBanner = useEnableCarrierInsuranceBanner();

  if (!enableCarrierInsuranceBanner) {
    return null;
  }

  // Check if user has any active automation rules with 'Insure the Package' action set to 'Carrier'
  const hasActiveCarrierInsuranceRules =
    automationRules?.some((rule) => {
      if (!rule.active) return false;
      return rule.orderRuleActions?.some((action) => {
        const carrierInsuranceProviderId = Number.parseInt(InsuranceProviders.Carrier.insuranceProviderId, 10);
        return (
          action.actionTypeId === ACTION_TYPE_INSURE_PACKAGE && action.intParameter === carrierInsuranceProviderId && !action.isDeleted
        );
      });
    }) ?? false;

  // Only show banner for US users with wallets configured and active carrier insurance rules
  if (homeCountry !== US || !walletEligibility || !hasActiveCarrierInsuranceRules) {
    return null;
  }

  return (
    <BannerMessageV2
      content={
        <FlexColumn>
          <Text textType={TextType.Paragraph} value={<FormattedMessage {...messages.description} />} />
          <FlexRow>
            <SwitchToParcelGuardButton
              automationRules={automationRules}
              className={style['switch-button']}
              refreshAutomationRules={refreshAutomationRules}
              theme={THEME_CONSTANTS.tertiary}
            />
            <span className={style['external-link']}>
              <FormattedMessage
                {...messages.learnMoreButton}
                values={{
                  a: (chunks) => (
                    <HelpArticleLink articleId={linkIDs.ParcelGuardKB}>
                      {chunks}
                      <FontAwesomeIcon className={style['external-link-icon']} icon={faExternalLinkAlt} />
                    </HelpArticleLink>
                  ),
                }}
              />
            </span>
          </FlexRow>
        </FlexColumn>
      }
      header={<Text textType={TextType.Label} value={<FormattedMessage {...messages.title} />} />}
      layout={BannerLayout.Full}
      status={BannerStatus.Info}
    />
  );
};
